/* =========================================================
   Seh-Check — vanilla JS state machine
   No frameworks, no jQuery dependency.
   ========================================================= */
(function () {
  'use strict';

  const SCREEN_ORDER = [
    'intro', 'setup', 'vision-r', 'vision-l', 'near', 'color', 'quiz', 'result'
  ];

  const STORAGE_KEY = 'mm_seh_check_state_v1';

  // -----------------------------------------------------------
  // Visus / optotype constants
  // -----------------------------------------------------------
  const TEST_DISTANCE_MM = 2000;                // 2 m for screening
  const VISUS_1_AT_2M_MM = 2.909;               // 5 arcmin at 2m
  const VISUS_SEQUENCE   = [0.3, 0.5, 0.63, 0.8, 1.0]; // 5 ascending levels
  const NEAR_SIZE_MM     = 4.0;                 // small at 30cm
  const NEAR_TRIALS      = 3;
  const COLOR_TRIALS     = 3;
  const SYMBOLS = ['apple', 'house', 'square', 'circle', 'star'];

  function visusToMm(visus) { return VISUS_1_AT_2M_MM / visus; }

  // -----------------------------------------------------------
  // Color plates (Ishihara-style)
  // High hue contrast so symbols are clearly visible to normal-sighted
  // users; colour-deficient users will see them less clearly.
  // -----------------------------------------------------------
  const COLOR_PLATES = [
    // Red-green: bright coral/red foreground on yellow-olive background
    { fg: ['#e63946', '#d62828', '#ff5b3b', '#c81d25'], bg: ['#c4d36a', '#aab753', '#d6e283', '#b3c25a'] },
    // Green vs warm coral: emerald on light salmon
    { fg: ['#1f9d70', '#2d8f5b', '#3aa57a', '#15825a'], bg: ['#ffb3a8', '#ffc7bc', '#fb9f90', '#ffa494'] },
    // Blue-yellow: cobalt blue on warm yellow
    { fg: ['#1d4ed8', '#2563eb', '#1e40af', '#3b5bdb'], bg: ['#fcd34d', '#fde68a', '#fbbf24', '#facc15'] }
  ];

  const SYMBOL_DRAWERS = {
    apple: (ctx) => {
      // stem on top + round body
      ctx.fillRect(47, 8, 6, 14);
      ctx.beginPath();
      ctx.ellipse(50, 60, 36, 38, 0, 0, Math.PI * 2);
      ctx.fill();
    },
    house: (ctx) => {
      // square base
      ctx.fillRect(18, 50, 64, 40);
      // triangular roof on top of base
      ctx.beginPath();
      ctx.moveTo(10, 50); ctx.lineTo(50, 14); ctx.lineTo(90, 50);
      ctx.closePath(); ctx.fill();
    },
    square: (ctx) => { ctx.fillRect(15, 15, 70, 70); },
    circle: (ctx) => {
      ctx.beginPath(); ctx.arc(50, 50, 38, 0, Math.PI * 2); ctx.fill();
    },
    star: (ctx) => {
      // classic 5-point star (10 alternating outer/inner vertices)
      const pts = [[50,10],[61,37],[90,39],[67,58],[75,86],[50,70],[25,86],[33,58],[10,39],[39,37]];
      ctx.beginPath();
      pts.forEach((p, i) => i === 0 ? ctx.moveTo(p[0], p[1]) : ctx.lineTo(p[0], p[1]));
      ctx.closePath(); ctx.fill();
    }
  };

  function generateColorPlate(symbolName, plate) {
    const SIZE = 260, PLATE_R = 122, SYM_SCALE = 1.55, CX = SIZE / 2, CY = SIZE / 2;
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = SIZE;
    const ctx = canvas.getContext('2d');

    ctx.fillStyle = 'black';
    ctx.fillRect(0, 0, SIZE, SIZE);
    ctx.fillStyle = 'white';
    ctx.save();
    ctx.translate(CX, CY);
    ctx.scale(SYM_SCALE, SYM_SCALE);
    ctx.translate(-50, -50);
    SYMBOL_DRAWERS[symbolName](ctx);
    ctx.restore();

    const data = ctx.getImageData(0, 0, SIZE, SIZE).data;
    const dots = [];
    const target = 460;
    let attempts = 0;
    const maxAttempts = target * 12;

    while (dots.length < target && attempts < maxAttempts) {
      attempts++;
      const angle = Math.random() * Math.PI * 2;
      const r = Math.sqrt(Math.random()) * (PLATE_R - 4);
      const x = CX + r * Math.cos(angle);
      const y = CY + r * Math.sin(angle);
      const dotR = 2.6 + Math.random() * 3.0;

      let overlap = false;
      for (let i = dots.length - 1; i >= 0 && i > dots.length - 60; i--) {
        const d = dots[i];
        const dx = d.x - x, dy = d.y - y;
        if (dx*dx + dy*dy < (d.r + dotR + 0.2) ** 2) { overlap = true; break; }
      }
      if (overlap) continue;

      const idx = (Math.round(y) * SIZE + Math.round(x)) * 4;
      const isSymbol = data[idx] > 128;
      const pool = isSymbol ? plate.fg : plate.bg;
      const color = pool[Math.floor(Math.random() * pool.length)];
      dots.push({ x, y, r: dotR, color });
    }

    const svgDots = dots.map(d =>
      `<circle cx="${d.x.toFixed(1)}" cy="${d.y.toFixed(1)}" r="${d.r.toFixed(1)}" fill="${d.color}"/>`
    ).join('');

    return `<svg width="${SIZE}" height="${SIZE}" viewBox="0 0 ${SIZE} ${SIZE}" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">${svgDots}</svg>`;
  }

  // -----------------------------------------------------------
  // State
  // -----------------------------------------------------------
  function defaultState() {
    return {
      step: 'intro',
      pixelsPerMm: null,
      ageGroup: null,
      visus: { right: null, left: null },
      near:  { errors: 0, done: false },
      color: { errors: 0 },
      quiz:  { yesCount: 0, familyHistory: false }
    };
  }

  // -----------------------------------------------------------
  // Helpers
  // -----------------------------------------------------------
  function shuffle(arr) {
    const a = arr.slice();
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  }
  function pickDistractors(correct, count) {
    return shuffle(SYMBOLS.filter(s => s !== correct)).slice(0, count);
  }

  // -----------------------------------------------------------
  // Main controller
  // -----------------------------------------------------------
  class SehCheck {
    constructor(root) {
      this.root = root;
      this.state = this.loadState();
      this.screens = root.querySelectorAll('[data-screen]');
      this.progressEl = root.querySelector('[data-progress]');
      this.bindEvents();
      this.initSetup();
      this.render();
      this.runEntryHook(this.state.step);
    }

    runEntryHook(step) {
      if (step === 'vision-r') this.showSubstep('prep-r');
      if (step === 'vision-l') this.showSubstep('prep-l');
      if (step === 'near')     this.startNear();
      if (step === 'color')    this.startColor();
      if (step === 'quiz')     this.startQuiz();
      if (step === 'result')   this.renderResult();
    }

    loadState() {
      try {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        if (raw) return Object.assign(defaultState(), JSON.parse(raw));
      } catch (_) {}
      return defaultState();
    }
    saveState() {
      try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(this.state)); } catch (_) {}
    }

    bindEvents() {
      this.root.addEventListener('click', (e) => {
        const next    = e.target.closest('[data-action="next"]');
        const back    = e.target.closest('[data-action="back"]');
        const restart = e.target.closest('[data-action="restart"]');
        const goto    = e.target.closest('[data-goto]');
        const sub     = e.target.closest('[data-substep]');
        const age     = e.target.closest('[data-age]');
        const calOk   = e.target.closest('[data-action="cal-ok"]');
        const skip    = e.target.closest('[data-action="skip-vision"]');
        const skipC   = e.target.closest('[data-action="skip-color"]');
        const startV  = e.target.closest('[data-action="start-vision"]');
        const print   = e.target.closest('[data-action="print"]');
        const quizAns = e.target.closest('[data-quiz-answer]');

        if (next)    { e.preventDefault(); this.go(+1); }
        if (back)    { e.preventDefault(); this.go(-1); }
        if (restart) { e.preventDefault(); this.reset(); }
        if (goto)    { e.preventDefault(); this.setStep(goto.dataset.goto); }
        if (sub)     { e.preventDefault(); this.showSubstep(sub.dataset.substep); }
        if (age)     { e.preventDefault(); this.selectAge(age.dataset.age); }
        if (calOk)   { e.preventDefault(); this.confirmCalibration(); }
        if (skip)    { e.preventDefault(); this.skipVisus(skip.dataset.eye); }
        if (skipC)   { e.preventDefault(); this.skipColor(); }
        if (startV)  { e.preventDefault(); this.startVisionTest(startV.dataset.eye); }
        if (print)   { e.preventDefault(); window.print(); }
        if (quizAns) { e.preventDefault(); this.answerQuiz(quizAns.dataset.quizAnswer); }
      });
    }

    currentIndex() {
      return Math.max(0, SCREEN_ORDER.indexOf(this.state.step));
    }

    go(delta) {
      const idx = this.currentIndex() + delta;
      if (idx < 0 || idx >= SCREEN_ORDER.length) return;
      this.setStep(SCREEN_ORDER[idx]);
    }

    setStep(step) {
      if (!SCREEN_ORDER.includes(step)) return;
      this.state.step = step;
      this.saveState();
      this.render();
      this.runEntryHook(step);
      this.root.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    startVisionTest(eye) {
      this.showSubstep(eye === 'right' ? 'test-r' : 'test-l');
      this.startVisus(eye);
    }

    reset() {
      this.state = defaultState();
      this.saveState();
      this.render();
      this.initSetup();
    }

    render() {
      this.screens.forEach((el) => {
        el.dataset.active = (el.dataset.screen === this.state.step) ? 'true' : 'false';
      });
      this.renderProgress();
    }

    renderProgress() {
      if (!this.progressEl) return;
      const idx = this.currentIndex();
      const stepsToShow = SCREEN_ORDER.slice(1);
      this.progressEl.innerHTML = '';
      stepsToShow.forEach((s, i) => {
        const real = i + 1;
        const dot = document.createElement('div');
        dot.className = 'sc-progress__step';
        if (real < idx) dot.dataset.state = 'done';
        else if (real === idx) dot.dataset.state = 'current';
        this.progressEl.appendChild(dot);
      });
      this.progressEl.style.display = (this.state.step === 'intro') ? 'none' : 'flex';
    }

    // --------------------------------------------------------
    // Setup screen — sub-steps
    // --------------------------------------------------------
    initSetup() {
      // calibration slider behaviour
      const slider = this.root.querySelector('[data-cal-slider]');
      const card   = this.root.querySelector('[data-cal-card]');
      if (slider && card) {
        const updateCard = () => {
          const widthMm = 85.6;
          const heightMm = 53.98;
          const px = parseFloat(slider.value);
          const ratio = px / widthMm;
          card.style.width  = px + 'px';
          card.style.height = (heightMm * ratio) + 'px';
        };
        slider.addEventListener('input', updateCard);
        updateCard();
      }
    }

    showSubstep(name) {
      const active = this.root.querySelector('.sc-screen[data-active="true"]');
      const scope = active || this.root;
      scope.querySelectorAll('[data-substep-target]').forEach(el => {
        el.dataset.active = (el.dataset.substepTarget === name) ? 'true' : 'false';
      });
    }

    selectAge(age) {
      this.state.ageGroup = age;
      this.saveState();
      this.root.querySelectorAll('[data-age]').forEach(el => {
        el.dataset.selected = (el.dataset.age === age) ? 'true' : 'false';
      });
      // advance to calibration after a brief pause
      setTimeout(() => this.showSubstep('calibrate'), 250);
    }

    confirmCalibration() {
      const slider = this.root.querySelector('[data-cal-slider]');
      if (!slider) return;
      const px = parseFloat(slider.value);
      this.state.pixelsPerMm = px / 85.6;
      this.saveState();
      this.showSubstep('distance');
    }

    // --------------------------------------------------------
    // Visus test
    // --------------------------------------------------------
    startVisus(eye) {
      this._visus = { eye, idx: 0, lastCorrect: null };
      this.renderVisusTrial();
    }

    skipVisus(eye) {
      // user can't continue → mark whatever they got so far
      this.state.visus[this._visus.eye] = this._visus.lastCorrect;
      this.saveState();
      this.go(+1);
    }

    renderVisusTrial() {
      const { eye, idx } = this._visus;
      if (idx >= VISUS_SEQUENCE.length) {
        this.state.visus[eye] = this._visus.lastCorrect;
        this.saveState();
        this.go(+1);
        return;
      }
      const visus = VISUS_SEQUENCE[idx];
      const mm = visusToMm(visus);
      const px = Math.max(8, Math.round(mm * (this.state.pixelsPerMm || 4)));
      const correct = SYMBOLS[Math.floor(Math.random() * SYMBOLS.length)];
      const choices = shuffle([correct, ...pickDistractors(correct, 3)]);

      const stage   = this.root.querySelector(`[data-visus-stage="${eye}"]`);
      const choiceR = this.root.querySelector(`[data-visus-choices="${eye}"]`);
      const trialEl = this.root.querySelector(`[data-visus-trial="${eye}"]`);

      if (trialEl) trialEl.textContent = `${idx + 1} / ${VISUS_SEQUENCE.length}`;

      stage.innerHTML = `<svg class="sc-optotype" width="${px}" height="${px}" aria-hidden="true"><use href="#sc-sym-${correct}"/></svg>`;

      choiceR.innerHTML = choices.map(c =>
        `<button class="sc-choice" data-symbol="${c}" type="button">
           <svg viewBox="0 0 100 100" aria-hidden="true"><use href="#sc-sym-${c}"/></svg>
         </button>`
      ).join('');

      const onClick = (e) => {
        const btn = e.target.closest('.sc-choice');
        if (!btn) return;
        choiceR.querySelectorAll('.sc-choice').forEach(b => b.disabled = true);
        const isCorrect = btn.dataset.symbol === correct;
        btn.dataset.feedback = isCorrect ? 'correct' : 'incorrect';
        if (isCorrect) this._visus.lastCorrect = visus;
        choiceR.removeEventListener('click', onClick);
        setTimeout(() => {
          this._visus.idx += 1;
          this.renderVisusTrial();
        }, 700);
      };
      choiceR.addEventListener('click', onClick);
    }

    // --------------------------------------------------------
    // Near check
    // --------------------------------------------------------
    startNear() {
      this._near = { idx: 0, errors: 0 };
      this.state.near = { errors: 0, done: false };
      this.renderNearTrial();
    }

    renderNearTrial() {
      const { idx } = this._near;
      if (idx >= NEAR_TRIALS) {
        this.state.near.errors = this._near.errors;
        this.state.near.done = true;
        this.saveState();
        this.go(+1);
        return;
      }
      const px = Math.max(10, Math.round(NEAR_SIZE_MM * (this.state.pixelsPerMm || 4)));
      const correct = SYMBOLS[Math.floor(Math.random() * SYMBOLS.length)];
      const choices = shuffle([correct, ...pickDistractors(correct, 3)]);

      const stage   = this.root.querySelector('[data-near-stage]');
      const choiceR = this.root.querySelector('[data-near-choices]');
      const trialEl = this.root.querySelector('[data-near-trial]');

      if (trialEl) trialEl.textContent = `${idx + 1} / ${NEAR_TRIALS}`;

      stage.innerHTML = `<svg class="sc-optotype" width="${px}" height="${px}" aria-hidden="true"><use href="#sc-sym-${correct}"/></svg>`;

      choiceR.innerHTML = choices.map(c =>
        `<button class="sc-choice" data-symbol="${c}" type="button">
           <svg viewBox="0 0 100 100" aria-hidden="true"><use href="#sc-sym-${c}"/></svg>
         </button>`
      ).join('');

      const onClick = (e) => {
        const btn = e.target.closest('.sc-choice');
        if (!btn) return;
        choiceR.querySelectorAll('.sc-choice').forEach(b => b.disabled = true);
        const isCorrect = btn.dataset.symbol === correct;
        btn.dataset.feedback = isCorrect ? 'correct' : 'incorrect';
        if (!isCorrect) this._near.errors += 1;
        choiceR.removeEventListener('click', onClick);
        setTimeout(() => {
          this._near.idx += 1;
          this.renderNearTrial();
        }, 700);
      };
      choiceR.addEventListener('click', onClick);
    }

    // --------------------------------------------------------
    // Color (Farb-Zauber) — Ishihara-style dot plates
    // --------------------------------------------------------
    startColor() {
      this._color = { idx: 0, errors: 0 };
      this.state.color = { errors: 0 };
      this.renderColorTrial();
    }

    skipColor() {
      this.state.color.errors = COLOR_TRIALS;
      this.saveState();
      this.go(+1);
    }

    renderColorTrial() {
      const { idx } = this._color;
      if (idx >= COLOR_TRIALS) {
        this.state.color.errors = this._color.errors;
        this.saveState();
        this.go(+1);
        return;
      }
      const plate = COLOR_PLATES[idx % COLOR_PLATES.length];
      const correct = SYMBOLS[Math.floor(Math.random() * SYMBOLS.length)];
      const choices = shuffle([correct, ...pickDistractors(correct, 3)]);

      const stage   = this.root.querySelector('[data-color-stage]');
      const choiceR = this.root.querySelector('[data-color-choices]');
      const trialEl = this.root.querySelector('[data-color-trial]');

      if (trialEl) trialEl.textContent = `${idx + 1} / ${COLOR_TRIALS}`;
      stage.innerHTML = generateColorPlate(correct, plate);

      choiceR.innerHTML = choices.map(c =>
        `<button class="sc-choice" data-symbol="${c}" type="button">
           <svg viewBox="0 0 100 100" aria-hidden="true"><use href="#sc-sym-${c}"/></svg>
         </button>`
      ).join('');

      const onClick = (e) => {
        const btn = e.target.closest('.sc-choice');
        if (!btn) return;
        choiceR.querySelectorAll('.sc-choice').forEach(b => b.disabled = true);
        const isCorrect = btn.dataset.symbol === correct;
        btn.dataset.feedback = isCorrect ? 'correct' : 'incorrect';
        if (!isCorrect) this._color.errors += 1;
        choiceR.removeEventListener('click', onClick);
        setTimeout(() => {
          this._color.idx += 1;
          this.renderColorTrial();
        }, 700);
      };
      choiceR.addEventListener('click', onClick);
    }

    // --------------------------------------------------------
    // Eltern-Quiz — 7 yes/no parent questions
    // --------------------------------------------------------
    startQuiz() {
      // Read localized question texts from the embedded JSON tag in the section.
      const dataEl = this.root.querySelector('[data-quiz-data]');
      let questions = [];
      try { questions = JSON.parse(dataEl ? dataEl.textContent : '[]'); } catch (_) {}
      this._quiz = { idx: 0, yesCount: 0, familyHistory: false, questions };
      this.state.quiz = { yesCount: 0, familyHistory: false };
      this.renderQuizQuestion();
    }

    renderQuizQuestion() {
      const { idx, questions } = this._quiz;
      const stage   = this.root.querySelector('[data-quiz-stage]');
      const trialEl = this.root.querySelector('[data-quiz-trial]');
      if (!stage) return;

      if (idx >= questions.length) {
        this.state.quiz.yesCount = this._quiz.yesCount;
        this.state.quiz.familyHistory = this._quiz.familyHistory;
        this.saveState();
        this.go(+1);
        return;
      }
      const q = questions[idx];
      if (trialEl) trialEl.textContent = `${idx + 1} / ${questions.length}`;
      stage.innerHTML = `<p class="sc-quiz__question">${q.text}</p>`;
    }

    answerQuiz(answer) {
      const { idx, questions } = this._quiz;
      const q = questions[idx];
      if (!q) return;
      if (answer === 'yes') {
        this._quiz.yesCount += 1;
        if (q.familyHistory) this._quiz.familyHistory = true;
      }
      this._quiz.idx += 1;
      this.renderQuizQuestion();
    }

    // --------------------------------------------------------
    // Result — synthesise traffic-light recommendation
    // --------------------------------------------------------
    renderResult() {
      const card  = this.root.querySelector('[data-result-card]');
      const meta  = this.root.querySelector('[data-result-meta]');
      const sumEl = this.root.querySelector('[data-result-summary]');
      if (!card) return;

      const labelsEl = this.root.querySelector('[data-result-labels]');
      let L = {};
      try { L = JSON.parse(labelsEl ? labelsEl.textContent : '{}'); } catch (_) {}

      const visusR = this.state.visus.right;
      const visusL = this.state.visus.left;
      const minVisus = (visusR != null && visusL != null)
        ? Math.min(visusR, visusL)
        : (visusR ?? visusL);

      // Per-mission status
      const visusStatus = (minVisus == null)         ? 'alert'
                        : (minVisus >= 0.8)          ? 'ok'
                        : (minVisus >= 0.5)          ? 'warn'
                                                     : 'alert';
      const nearStatus  = (this.state.near.errors >= 2) ? 'alert'
                        : (this.state.near.errors === 1) ? 'warn' : 'ok';
      const colorStatus = (this.state.color.errors >= 2) ? 'alert'
                        : (this.state.color.errors === 1) ? 'warn' : 'ok';
      const quizStatus  = (this.state.quiz.yesCount >= 3 || this.state.quiz.familyHistory)
                        ? 'alert'
                        : (this.state.quiz.yesCount >= 1) ? 'warn' : 'ok';

      // Overall tone
      const statuses = [visusStatus, nearStatus, colorStatus, quizStatus];
      const tone = statuses.includes('alert') ? 'red'
                 : statuses.includes('warn')  ? 'yellow'
                                              : 'green';

      const toneLabel = (L.tone && L.tone[tone]) || tone;
      const toneCopy  = (L.tone_copy && L.tone_copy[tone]) || '';

      if (sumEl && toneCopy) sumEl.textContent = toneCopy;

      const visusFmt = (v) => v == null ? '—' : v.toFixed(2);
      const missions = [
        {
          status: visusStatus,
          title: L.missions?.visus || 'Sehschärfe',
          body:  `R: ${visusFmt(visusR)} · L: ${visusFmt(visusL)}`
        },
        {
          status: nearStatus,
          title: L.missions?.near || 'Nah-Sicht',
          body:  `${this.state.near.errors} ${L.errors || 'Fehler'} / ${NEAR_TRIALS}`
        },
        {
          status: colorStatus,
          title: L.missions?.color || 'Farb-Zauber',
          body:  `${this.state.color.errors} ${L.errors || 'Fehler'} / ${COLOR_TRIALS}`
        },
        {
          status: quizStatus,
          title: L.missions?.quiz || 'Eltern-Quiz',
          body:  `${this.state.quiz.yesCount}× ${L.yes || 'Ja'}${this.state.quiz.familyHistory ? ' · ' + (L.family || 'Familie') : ''}`
        }
      ];

      card.innerHTML = `
        <div class="sc-result__light" data-tone="${tone}">${toneLabel}</div>
        <div class="sc-result__missions">
          ${missions.map(m => `
            <div class="sc-result__mission" data-status="${m.status}">
              <h4>${m.title}</h4>
              <p>${m.body}</p>
            </div>
          `).join('')}
        </div>
      `;

      if (meta) {
        const stamp = new Date().toLocaleDateString();
        meta.textContent = `${L.taken_on || 'Durchgeführt am'} ${stamp}`;
      }
    }
  }

  // -----------------------------------------------------------
  // Boot
  // -----------------------------------------------------------
  function init() {
    document.querySelectorAll('[data-seh-check]').forEach((el) => new SehCheck(el));
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
